/**
 * "Asuntos sin atender" inbox contract.
 *
 * Cross-source list of every actionable matter that currently waits on the
 * logged-in user across Slack, Jira, GitHub and Daily. Mirrors the existing
 * Slack ball-in-court model and adds explicit ownership-based reasons plus
 * an AI-elevated `domain-context` for Slack threads. Pure helpers here are
 * shared between apps/api and apps/web; no BFF or UI logic lives in this file.
 */
export const DEFAULT_UNATTENDED_COUNTS = {
    slack: 0,
    jira: 0,
    github: 0,
    daily: 0,
};
export const UNATTENDED_SOURCE_LABELS = {
    slack: 'Slack',
    jira: 'Jira',
    github: 'GitHub',
    daily: 'Daily',
};
export function isUnattendedEntry(entry) {
    return entry.ballInCourt === 'me';
}
export function summarizeInboxCounts(entries) {
    const counts = { ...DEFAULT_UNATTENDED_COUNTS };
    for (const entry of entries) {
        counts[entry.source] += 1;
    }
    return counts;
}
function priorityWeight(priority) {
    if (priority === 'high')
        return 0;
    if (priority === 'medium')
        return 1;
    return 2;
}
function reasonWeight(reason) {
    switch (reason) {
        case 'mine-blocker':
        case 'mine-blocker-line':
        case 'mine-ci-failing':
        case 'mine-author-changes-requested':
            return 0;
        case 'mine-direct-mention':
        case 'mine-dm':
        case 'mine-reminder-due-today':
        case 'mine-reviewer-requested':
        case 'mine-subtask-verify':
            return 1;
        case 'mine-assigned':
        case 'mine-thread-waiting':
        case 'mine-watch-blocked':
        case 'mine-reminder-overdue':
            return 2;
        case 'mine-implicit':
        case 'mine-domain-context':
            return 3;
        default:
            return 4;
    }
}
export function rankUnattendedEntries(entries, nowMs = Date.now()) {
    const enriched = entries.map((entry) => {
        const updatedMs = Date.parse(entry.updatedAt);
        const dueMs = entry.dueAt ? Date.parse(entry.dueAt) : Number.POSITIVE_INFINITY;
        return {
            entry,
            priority: priorityWeight(entry.priority),
            reason: reasonWeight(entry.unattendedReason),
            updatedMs: Number.isFinite(updatedMs) ? updatedMs : 0,
            dueMs: Number.isFinite(dueMs) ? dueMs : Number.POSITIVE_INFINITY,
            overdue: dueMs < nowMs,
        };
    });
    enriched.sort((a, b) => {
        if (a.overdue !== b.overdue)
            return a.overdue ? -1 : 1;
        if (a.priority !== b.priority)
            return a.priority - b.priority;
        if (a.reason !== b.reason)
            return a.reason - b.reason;
        if (a.dueMs !== b.dueMs)
            return a.dueMs - b.dueMs;
        return b.updatedMs - a.updatedMs;
    });
    return enriched.map((row) => row.entry);
}
export function emptyUnattendedInbox(now = new Date().toISOString()) {
    return {
        entries: [],
        counts: { ...DEFAULT_UNATTENDED_COUNTS },
        generatedAt: now,
    };
}
export function unattendedChip(entry) {
    if (entry.ballInCourt === 'me') {
        if (entry.unattendedReason === 'mine-blocker') {
            return { label: 'Bloqueante', tone: 'warn' };
        }
        if (entry.unattendedReason === 'mine-domain-context') {
            return { label: 'Por contexto', tone: 'accent' };
        }
        return { label: 'Te toca', tone: 'warn' };
    }
    if (entry.ballInCourt === 'them') {
        return { label: 'Esperando a otros', tone: 'flat' };
    }
    return { label: 'Sin cancha', tone: 'flat' };
}
export function buildUnattendedRefId(source, ...parts) {
    return `${source}:${parts.join(':')}`;
}
//# sourceMappingURL=unattendedInbox.js.map